import { useMemo } from "react";
import InfoTooltip from "./InfoTooltip";

// The stats are computed twice: once over everything, and once over just what
// the filter leaves in. Both passes are the same arithmetic, so they share this
// function and differ only in whether the in_filter flags are consulted.
//
// person_trip.in_filter already means "this person and this trip are both in
// the filter", so it is the only flag the person_trip level needs to check.
const compute_stats = (people, trips, filtered) => {
  const included = (x) => !filtered || x.in_filter;
  const people_list = Object.values(people).filter(included);
  const trips_list = Object.values(trips).filter(included);

  const total_voyagers = people_list.reduce((acc, p) => acc + p.group_size, 0);

  const total_voyages = trips_list.length;

  const total_voyager_voyages = people_list.reduce(
    (acc, p) => acc + p.group_size * p.person_trips.filter(included).length, 0);

  // A "connection" is a pair of people who shared at least one trip. A pair that
  // travelled together more than once still only counts once, so collect the
  // distinct pairs rather than summing n-choose-2 over the trips.
  const pairs = new Map();
  for (const trip of trips_list) {
    const person_trips = trip.person_trips.filter(included);
    for (let i = 0; i < person_trips.length; i++) {
      for (let j = i + 1; j < person_trips.length; j++) {
        const [i1, p1] = [person_trips[i].person_index, person_trips[i].person];
        const [i2, p2] = [person_trips[j].person_index, person_trips[j].person];
        const pair_key = i1 < i2 ? `${i1}-${i2}` : `${i2}-${i1}`;
        pairs.set(pair_key, p1.group_size * p2.group_size);
      }
    }
  }
  const total_connections = [...pairs.values()].reduce((acc, n) => acc + n, 0);

  return { total_voyagers, total_voyages, total_voyager_voyages, total_connections };
};

// The same funnel as the filter menu button, at text size, to mark the
// bracketed number as "this is the count under the current filter".
const filter_icon = (
  <svg className="info_filter_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="4 4 20 4 14 12 14 19 10 21 10 12 4 4" />
  </svg>
);

const MenuInfo = ({ active, people, trips, close_menu, nora_mode, set_nora_mode }) => {

  const stats = useMemo(() => compute_stats(people, trips, false), [people, trips]);

  // Nothing is filtered out when the filter is at its default settings, and
  // then there is no second number worth showing.
  const filter_active = useMemo(() =>
    Object.values(people).some((p) => !p.in_filter) || Object.values(trips).some((t) => !t.in_filter),
    [people, trips]);

  const filtered_stats = useMemo(() => filter_active ? compute_stats(people, trips, true) : null,
    [people, trips, filter_active]);

  const stat_value = (key) => (
    <div className="info_value">
      {stats[key]}
      {filtered_stats && <span className="info_value_filtered">{"\u2002"}({filter_icon}{filtered_stats[key]})</span>}
    </div>
  );

  return (
    <div className={"menu_panel info_panel" + (active ? "" : " menu_panel_hidden")}
         onClick={close_menu}>
      <label className="info_label">Total Voyagers</label>
      {stat_value("total_voyagers")}
      <label className="info_label">Total Voyages</label>
      {stat_value("total_voyages")}
      <label className="info_label">
        Total Voyager Voyages
        <InfoTooltip visible={active}
                     text="Sum across all voyagers, where a voyager with n trips contributes n to the sum" />
      </label>
      {stat_value("total_voyager_voyages")}
      <label className="info_label">
        Total Connections
        <InfoTooltip visible={active}
                     text="Number of distinct pairs of voyagers who shared at least one voyage" />
      </label>
      {stat_value("total_connections")}
      <button className="nora_button" onClick={() => set_nora_mode(!nora_mode)}>
        {nora_mode ? "Exit Nora Mode" : "Nora Mode"}
      </button>
    </div>
  );
};

export default MenuInfo;
